import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Chip,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";

import CustomizedDataGrid from "../components/CustomizedDataGrid";
import { getJobs, deleteJob } from "../api/jobRoutes";
import { JOB_TYPE_LABEL } from "../constants/constants";

export default function Jobs() {
  const navigate = useNavigate();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [filterData, setFilterData] = useState({
    page: 1,
    size: 10,
  });

  const [rowCount, setRowCount] = useState(0);

  useEffect(() => {
    loadJobs(filterData);
  }, [filterData]);

  const loadJobs = async (filterData) => {
    try {
      const response = await getJobs(filterData);
      setJobs(response.data);
      setRowCount(response.total);
    } catch (error) {

    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setFilterData((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleRowsPerPageChange = (newSize) => {
    setFilterData((prev) => ({
      ...prev,
      size: newSize,
      page: 1,
    }));
  };

  const handleRowClick = (params) => {
    navigate(`/editor/${params.row.id}`);
  };

  const handleMenuOpen = (event, jobId) => {
    event.stopPropagation();

    setAnchorEl(event.currentTarget);
    setSelectedJobId(jobId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedJobId(null);
  };

  const handleDelete = async () => {
    try {
      await deleteJob(selectedJobId);

      setJobs((prev) =>
        prev.filter((job) => job.id !== selectedJobId)
      );

      handleMenuClose();
    } catch (error) {
    }
  };

  const columns = [
    {
      field: "id",
      headerName: "Job ID",
      flex: 1,
      minWidth: 240,
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 240,
    },
    {
      field: "type",
      headerName: "Type",
      width: 140,
      renderCell: (params) => (
        <Typography sx={{ textTransform: "capitalize" }}>
          {JOB_TYPE_LABEL[params.value] || params.value}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 140,
      renderCell: (params) => {
        const colorMap = {
          pending: "default",
          processing: "info",
          completed: "success",
          failed: "error",
        };

        return (
          <Chip
            label={params.value}
            color={colorMap[params.value] || "default"}
            size="small"
            sx={{ textTransform: "capitalize" }}
          />
        );
      },
    },
    {
      field: "created_at",
      headerName: "Created At",
      flex: 1,
      minWidth: 180,
      valueFormatter: (value) =>
        new Date(value).toLocaleString(),
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      width: 80,
      align: "center",
      renderCell: (params) => (
        <>
          <IconButton
            onClick={(event) =>
                handleMenuOpen(event, params.row.id)
            }
            sx={{
                border: "none",
                borderRadius: 2,
                backgroundColor: "transparent",
                transition: "all 0.2s ease",

                "&:hover": {
                backgroundColor: "action.hover",
                },
            }}
            >
            <MoreVertIcon fontSize="small" />
            </IconButton>
        </>
      ),
    },
  ];

  return (
    <>
      <CustomizedDataGrid
        rows={jobs}
        columns={columns}
        loading={loading}
        checkboxSelection
        onRowClick={handleRowClick}
        rowCount={rowCount}
        page={filterData.page}
        pageSize={filterData.size}
        handlePageChange={handlePageChange}
        handleRowsPerPageChange={handleRowsPerPageChange}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>

          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}